import { create } from 'zustand';

/**
 * Transient user notifications surfaced as a *deck-of-cards* stack at the
 * bottom-right (see {@link ToastHost}). Order is FIFO: `toasts[0]` is the
 * front of the deck and the one currently in view. The host runs the
 * auto-dismiss timer for ONLY the front card; when it expires (or the user
 * clicks ✕) the next toast becomes the front and starts its own timer.
 *
 * Variants by duration:
 *   - default → `DEFAULT_TOAST_MS` (~5s)
 *   - explicit `ms` → custom duration (fast vs slow)
 *   - `sticky: true` (or `ms: 0`) → no auto-dismiss; the user MUST close
 *     this toast manually. Sticky toasts hold up the queue — newer
 *     notifications wait behind them until they're dismissed. This is
 *     intentional for "cannot lose" prompts.
 *
 * The store deliberately does NOT schedule timers — that responsibility
 * lives in the host so the timer can be paused on hover and re-armed when
 * the active card changes. The store is just a FIFO queue.
 */

export type ToastKind = 'info' | 'error';

export interface ToastAction {
  /** Button label. */
  label: string;
  /** Invoked when the user clicks the action. The handler decides whether
   *  to dismiss the toast (clicking an action does NOT auto-dismiss). */
  onClick: () => void;
  /** Render as the primary button (filled). At most one per toast. */
  primary?: boolean;
}

export interface Toast {
  id: number;
  message: string;
  kind: ToastKind;
  /** Auto-dismiss duration in ms. Omit (or pass `0`) to make the toast
   *  sticky — equivalent to `sticky: true`. Default applied by the host
   *  when neither this nor `sticky` is set: {@link DEFAULT_TOAST_MS}. */
  ms?: number;
  /** True → no auto-dismiss timer; user must close manually. Sticky
   *  toasts block the queue (newer toasts wait behind). */
  sticky?: boolean;
  /** Optional action buttons rendered in the toast (e.g. "Update now" /
   *  "Later"). Clicking an action does NOT auto-dismiss; the handler
   *  decides. */
  actions?: ToastAction[];
}

export interface ToastStore {
  toasts: Toast[];
  /** Append a toast to the back of the deck. Returns its id. */
  push: (
    message: string,
    opts?: {
      kind?: ToastKind;
      ms?: number;
      sticky?: boolean;
      actions?: ToastAction[];
    },
  ) => number;
  /** Remove a toast by id (auto-dismiss timer, manual close, or programmatic). */
  dismiss: (id: number) => void;
  /** Clear every queued toast — used by tests and by the close-window guard. */
  clear: () => void;
  /** Clear only non-sticky toasts (the "Clear all" affordance on the host).
   *  Sticky toasts persist so a critical prompt cannot be wiped by accident. */
  dismissTransient: () => void;
}

/** Default auto-dismiss window for non-sticky toasts. Exported so the host
 *  can honor the same default. */
export const DEFAULT_TOAST_MS = 5000;

let _nextId = 1;

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  push: (message, opts) => {
    const id = _nextId++;
    const kind = opts?.kind ?? 'info';
    const sticky = opts?.sticky === true || opts?.ms === 0;
    const toast: Toast = {
      id,
      message,
      kind,
      ...(opts?.ms !== undefined ? { ms: opts.ms } : {}),
      ...(sticky ? { sticky: true } : {}),
      ...(opts?.actions ? { actions: opts.actions } : {}),
    };
    set((s) => ({ toasts: [...s.toasts, toast] }));
    return id;
  },
  dismiss: (id) => {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
  clear: () => set({ toasts: [] }),
  dismissTransient: () =>
    set((s) => {
      const sticky = s.toasts.filter((t) => t.sticky);
      // Reference-equality early-out so subscribers don't re-render.
      if (sticky.length === s.toasts.length) return s;
      return { toasts: sticky };
    }),
}));

/**
 * Convenience: returns a bound `toast(message, opts)` function that closes
 * over the current store. Use this inside non-component code paths
 * (`fsActions`, async helpers) so call sites don't need to subscribe.
 */
export function toast(
  message: string,
  opts?: {
    kind?: ToastKind;
    ms?: number;
    sticky?: boolean;
    actions?: ToastAction[];
  },
): number {
  return useToastStore.getState().push(message, opts);
}
